import { useState, useEffect, useCallback } from 'react';

const SETTINGS_PREFIX = 'inventory_columns_';

export function useColumnSettings(storageKey, organization = '') {
  const fullKey = `${SETTINGS_PREFIX}${storageKey}_${organization}`;

  const [columnSettings, setColumnSettings] = useState(() => {
    try {
      const saved = localStorage.getItem(fullKey);
      if (saved) return JSON.parse(saved);
    } catch { /* ignore corrupt data */ }
    return {};
  });

  const [sortConfig, setSortConfig] = useState(() => {
    try {
      const saved = localStorage.getItem(fullKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.sort || { key: null, direction: 'asc' };
      }
    } catch { /* ignore */ }
    return { key: null, direction: 'asc' };
  });

  // Persist on change
  useEffect(() => {
    try {
      localStorage.setItem(fullKey, JSON.stringify({ ...columnSettings, sort: sortConfig }));
    } catch { /* storage full */ }
  }, [fullKey, columnSettings, sortConfig]);

  const toggleColumnVisibility = useCallback((key) => {
    setColumnSettings(prev => ({
      ...prev,
      [key]: { ...prev[key], visible: !(prev[key]?.visible ?? true) },
    }));
  }, []);

  const moveColumnUp = useCallback((key, columns) => {
    const idx = columns.findIndex(c => (c.key || c.accessorKey) === key);
    if (idx <= 0) return;
    const newCols = [...columns];
    [newCols[idx - 1], newCols[idx]] = [newCols[idx], newCols[idx - 1]];
    const newSettings = {};
    newCols.forEach((col, i) => {
      const k = col.key || col.accessorKey;
      newSettings[k] = { ...(columnSettings[k] || {}), order: i };
    });
    setColumnSettings(newSettings);
  }, [columnSettings]);

  const moveColumnDown = useCallback((key, columns) => {
    const idx = columns.findIndex(c => (c.key || c.accessorKey) === key);
    if (idx >= columns.length - 1) return;
    const newCols = [...columns];
    [newCols[idx], newCols[idx + 1]] = [newCols[idx + 1], newCols[idx]];
    const newSettings = {};
    newCols.forEach((col, i) => {
      const k = col.key || col.accessorKey;
      newSettings[k] = { ...(columnSettings[k] || {}), order: i };
    });
    setColumnSettings(newSettings);
  }, [columnSettings]);

  const handleSort = useCallback((key) => {
    setSortConfig(prev => {
      const dir = prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction: dir };
    });
  }, []);

  const resetColumnSettings = useCallback(() => {
    try { localStorage.removeItem(fullKey); } catch { /* ignore */ }
    setColumnSettings({});
    setSortConfig({ key: null, direction: 'asc' });
  }, [fullKey]);

  return {
    columnSettings,
    sortConfig,
    toggleColumnVisibility,
    moveColumnUp,
    moveColumnDown,
    handleSort,
    resetColumnSettings,
  };
}
